import React from 'react';
import { Box, Typography } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PropagateLoader from 'react-spinners/PropagateLoader';

import { Message } from 'components/Message';
import { Scrollable } from 'components/Scrollable';
import { StyledMarkdown } from '../StyledMarkdown';
import { ChatMessage, ChatMessageError, ChatMessageStatus, ChatSession } from 'types/types';

interface ResumeChatMessagesProps {
  chatSession: ChatSession;
  messages: ChatMessage[];
  processingMessage: ChatMessageStatus | ChatMessageError | null;
  streamingMessage: ChatMessage | null;
  streaming: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

export function ResumeChatMessages({
  chatSession,
  messages,
  processingMessage,
  streamingMessage,
  streaming,
  messagesEndRef,
}: ResumeChatMessagesProps): React.ReactElement {
  return (
    <Scrollable
      sx={{
        position: 'relative',
        boxSizing: 'border-box',
        maxHeight: '100%',
        maxWidth: '100%',
        width: '100%',
        display: 'flex',
        flexGrow: 1,
        flex: 1,
        overflowY: 'auto',
        p: 0,
        gap: 1,
      }}
    >
      {messages.map((message: ChatMessage) => (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            maxWidth: '100%',
            position: 'relative',
            '& pre, & > p': { border: 0, mt: '0.25rem', mb: 0 },
          }}
        >
          {message.role === 'user' && (
            <>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography>{message.content}</Typography>
            </>
          )}
          {message.role === 'assistant' && (
            <>
              {message.extraContext?.isAnswer ? (
                <>
                  <QuestionAnswerIcon sx={{ mr: 1 }} />
                  <StyledMarkdown content={message.content} />
                </>
              ) : (
                <>
                  <PrecisionManufacturingIcon sx={{ mr: 1 }} />
                  <StyledMarkdown content="Resume updated." />
                </>
              )}
            </>
          )}
        </Box>
      ))}
      {processingMessage !== null && (
        <Message chatSession={chatSession} message={processingMessage} />
      )}
      {streamingMessage !== null && (
        <Box sx={{ '& pre': { border: 0 } }}>
          <StyledMarkdown content={streamingMessage.content} streaming={true} />
        </Box>
      )}
      {streaming && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            m: 1,
          }}
        >
          <PropagateLoader
            size="10px"
            loading={streaming}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </Box>
      )}
      <div ref={messagesEndRef} />
    </Scrollable>
  );
}
